import Fuse, { type IFuseOptions } from "fuse.js";
import type { Guide, SearchResult, SearchMatch } from "./types";

/**
 * Configuration for the fuse.js search index.
 */
const FUSE_OPTIONS: IFuseOptions<Guide> = {
  // Fields to search with their weights (higher = more important)
  keys: [
    { name: "title", weight: 2.0 },
    { name: "keywords", weight: 1.5 },
    { name: "content", weight: 1.0 },
  ],
  // Include match information for highlighting
  includeMatches: true,
  // Include score for ranking
  includeScore: true,
  // Fuzzy matching threshold (0 = exact, 1 = anything matches)
  threshold: 0.4,
  // Ignore location in string (search whole content)
  ignoreLocation: true,
  // Minimum characters before search kicks in
  minMatchCharLength: 2,
  // Use extended search for better matching
  useExtendedSearch: false,
  // Find individual words
  findAllMatches: true,
};

/**
 * Guide search interface returned by createGuideSearch.
 */
export interface GuideSearch {
  /** Search guides by query string */
  search: (query: string) => SearchResult[];
  /** Get a single guide by slug */
  getGuide: (slug: string) => Guide | undefined;
  /** Get all guides sorted by order */
  getAllGuides: () => Guide[];
}

/**
 * Creates a searchable guide index.
 *
 * @param guides - Array of parsed guide objects
 * @returns Search interface with search, getGuide, and getAllGuides functions
 */
export function createGuideSearch(guides: Guide[]): GuideSearch {
  // Create fuse.js index
  const fuse = new Fuse(guides, FUSE_OPTIONS);

  // Create lookup map for O(1) access by slug
  const guideMap = new Map(guides.map((g) => [g.slug, g]));

  // Pre-sort guides by order for table of contents
  const sortedGuides = [...guides].sort((a, b) => a.order - b.order);

  return {
    search(query: string): SearchResult[] {
      if (!query.trim()) {
        return [];
      }

      const fuseResults = fuse.search(query);

      return fuseResults.map((result) => ({
        guide: result.item,
        score: result.score ?? 0,
        matches: (result.matches ?? []).map(
          (match): SearchMatch => ({
            key: match.key ?? "",
            indices: match.indices as ReadonlyArray<readonly [number, number]>,
          })
        ),
      }));
    },

    getGuide(slug: string): Guide | undefined {
      return guideMap.get(slug);
    },

    getAllGuides(): Guide[] {
      return [...sortedGuides];
    },
  };
}
